import {
  isFrameInFocus,
  showTaskFocusTile,
  taskFocusFrameId,
} from "@/lib/task-focus-tile";
/**
 * The task pocket's header (spec §6.5, item 1; Part 10 §4): the task's
 * card itself, painted, with its ticket, title, status and assignee, and
 * on its eyebrow row Open conversation, the More menu and Close. More
 * holds Stop or Resume the agent, Open parent conversation, Show
 * deliverables, Show source note, More details and Delete task.
 */
import { useCallback, useState, type ReactNode } from "react";
import { getCurrentDenkerWs } from "@/api/canvas-ws-ref";
import { fetchDeliverables, type Task } from "@/api/tasks";
import {
  Button,
  CONCEPT_ICONS,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  getFrameTypeIcon,
  Icons,
} from "@/components/ui";
import {
  TASK_PAINT_BUTTON_CLASS,
  TaskCardFace,
} from "@/components/shapes/shared/paint-card-faces";
import { taskAssigneeName } from "@/components/shapes/taskboard-frame/task-state";
import { useTaskWorkerActions } from "@/hooks/use-task-worker-actions";
import { translate, useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { panelsTasksMessages } from "@/i18n/messages/panels-tasks";
import { activateFrameInCurrentContext } from "@/lib/focus-frame-activation";
import { workspaceViewAdapter } from "@/lib/workspace-view-adapter";
import { useCanvasStore } from "@/stores/canvas-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { useFramesStore } from "@/stores/frames-store";
import type { ActiveExecution } from "@/stores/companion-store";
import { useNotificationStore } from "@/stores/notification-store";
import { deleteProjectTask, showTaskMoreDetails } from "./task-actions";

interface AgentControl {
  action: "pause" | "resume";
  disabled: boolean;
  onClick: () => void;
}

function toastWorkerActionError(message: string) {
  useNotificationStore.getState().addSystemToast({
    id: "task-worker-action-error",
    title: translate(panelsTasksMessages, "workerActionFailed"),
    description: message,
    level: "error",
  });
}

/** Stop or Resume through the task execution when the worker has no native control. */
function executionControl(
  execution: ActiveExecution | undefined,
): AgentControl | null {
  if (!execution) return null;
  if (
    execution.status === "running" ||
    execution.status === "waiting_approval"
  ) {
    return {
      action: "pause",
      disabled: false,
      onClick: () => getCurrentDenkerWs()?.stopExecution(execution.id),
    };
  }
  if (execution.status === "paused") {
    return {
      action: "resume",
      disabled: false,
      onClick: () => getCurrentDenkerWs()?.resumeExecution(execution.id),
    };
  }
  return null;
}

export function TaskPocketHeader({
  projectId,
  task,
  taskExecution,
  hasDeliverables,
  boardFrameId = null,
  title,
  onClose,
}: {
  projectId: string;
  task: Task;
  /** The title, which the pocket edits in place on the card. */
  title: ReactNode;
  taskExecution: ActiveExecution | undefined;
  hasDeliverables: boolean;
  boardFrameId?: string | null;
  onClose: () => void;
}) {
  const t = useT(panelsTasksMessages);
  const tc = useT(commonMessages);
  const { worker, busy, run, control } = useTaskWorkerActions(
    projectId,
    task.id,
    toastWorkerActionError,
  );
  const sourceNoteId = useFramesStore((state) =>
    task.source === "note" && task.frame && state.framesById[task.frame]
      ? task.frame
      : null,
  );
  const openConversation = useCallback(() => {
    const { presentation } = usePresentationStore.getState();
    if (boardFrameId && isFrameInFocus(boardFrameId, presentation)) {
      const tile = taskFocusFrameId(task, useFramesStore.getState());
      if (tile) {
        showTaskFocusTile(boardFrameId, tile);
        return;
      }
    }
    void run("open");
  }, [boardFrameId, task, run]);
  const boardInFocus = usePresentationStore(
    (state) =>
      !!boardFrameId && isFrameInFocus(boardFrameId, state.presentation),
  );
  const focusedTile = useFramesStore((state) =>
    boardInFocus ? taskFocusFrameId(task, state) : null,
  );
  const canOpenConversation = !!worker?.conversation_id || !!focusedTile;
  const [deleting, setDeleting] = useState(false);
  const agentControl: AgentControl | null =
    control ?? executionControl(taskExecution);
  const ChatIcon = CONCEPT_ICONS.chat;
  const NoteIcon = getFrameTypeIcon("note");

  const handleShowDeliverables = useCallback(async () => {
    try {
      const deliverables = await fetchDeliverables(projectId, task.id);
      workspaceViewAdapter().revealFrames(
        deliverables.map((deliverable) => deliverable.frame),
      );
    } catch {
      toastWorkerActionError(t("deliverablesFailed"));
    }
  }, [projectId, task.id, t]);

  const handleShowNote = useCallback(() => {
    if (!sourceNoteId) return;
    if (!activateFrameInCurrentContext(sourceNoteId)) {
      useCanvasStore.getState().selectFrame(sourceNoteId);
    }
  }, [sourceNoteId]);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    if (!(await deleteProjectTask(projectId, task.id))) setDeleting(false);
  }, [projectId, task.id]);

  // The pocket shows this task, so the page turns in place.
  const handleMoreDetails = useCallback(() => {
    void showTaskMoreDetails({ id: task.id, projectId, projectName: null });
  }, [projectId, task.id]);

  const actions = (
    <>
      {canOpenConversation ? (
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          className={TASK_PAINT_BUTTON_CLASS}
          onClick={openConversation}
          disabled={busy}
          aria-label={t("openConversation")}
          title={t("openConversation")}
          data-testid="task-pocket-open-conversation"
        >
          <ChatIcon className="h-4 w-4" aria-hidden="true" />
        </Button>
      ) : null}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            className={TASK_PAINT_BUTTON_CLASS}
            aria-label={t("more")}
            title={t("more")}
            data-testid="task-pocket-more"
          >
            <Icons.MoreHorizontal className="h-4 w-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" data-task-pocket-owned="">
          {agentControl?.action === "pause" ? (
            <DropdownMenuItem
              icon={<Icons.Square />}
              disabled={agentControl.disabled}
              onSelect={agentControl.onClick}
              data-testid="task-pocket-stop"
            >
              {t("stopAgent")}
            </DropdownMenuItem>
          ) : null}
          {agentControl?.action === "resume" ? (
            <DropdownMenuItem
              icon={<Icons.Play />}
              disabled={agentControl.disabled}
              onSelect={agentControl.onClick}
              data-testid="task-pocket-resume"
            >
              {t("resumeAgent")}
            </DropdownMenuItem>
          ) : null}
          {worker?.parent_conversation_id ? (
            <DropdownMenuItem
              icon={<ChatIcon />}
              disabled={busy}
              onSelect={() => void run("open-parent")}
              data-testid="task-pocket-open-parent"
            >
              {t("openParentConversation")}
            </DropdownMenuItem>
          ) : null}
          {hasDeliverables ? (
            <DropdownMenuItem
              icon={<Icons.GalleryHorizontal />}
              onSelect={() => void handleShowDeliverables()}
              data-testid="task-pocket-show-deliverables"
            >
              {t("showDeliverables")}
            </DropdownMenuItem>
          ) : null}
          {sourceNoteId ? (
            <DropdownMenuItem
              icon={<NoteIcon />}
              onSelect={handleShowNote}
              data-testid="task-pocket-show-note"
            >
              {t("showSourceNote")}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            icon={<Icons.Info />}
            onSelect={handleMoreDetails}
            data-testid="task-pocket-more-details"
          >
            {t("moreDetails")}
          </DropdownMenuItem>
          <DropdownMenuSeparator className="h-2 bg-transparent" />
          <DropdownMenuItem
            destructive
            icon={<Icons.Trash2 />}
            disabled={deleting}
            onSelect={() => void handleDelete()}
            data-testid="task-pocket-delete"
          >
            {t("deleteTask")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Button
        type="button"
        variant="ghost"
        size="iconSm"
        className={TASK_PAINT_BUTTON_CLASS}
        onClick={onClose}
        aria-label={tc("close")}
        title={tc("close")}
        data-testid="task-pocket-close"
      >
        <Icons.X className="h-4 w-4" aria-hidden="true" />
      </Button>
    </>
  );

  return (
    <TaskCardFace
      eyebrow={t("task")}
      ticket={task.ticket_display}
      title={title}
      status={task.status}
      assigneeName={taskAssigneeName(task)}
      actions={actions}
      className="mb-1.5 rounded-preview [font-size:16px]"
      testId="task-pocket-header"
      testIds={{
        ticket: "task-pocket-ticket",
        status: "task-pocket-status-chip",
      }}
    />
  );
}
